import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon, StarIcon } from 'lucide-react';
import Button from './Button';
import Wizard from './Wizard';
const caseStudies = [{
  id: 'fashion',
  title: 'How LuxeStyle Increased Conversions by 47% with AI Shopping Advisors',
  category: 'Fashion & Apparel',
  company: 'LuxeStyle',
  logo: 'https://images.unsplash.com/photo-1560243563-062bfc001d68?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  image: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  challenge: 'LuxeStyle, a premium fashion retailer, struggled with high cart abandonment rates (68%) and difficulty showcasing their extensive catalog effectively across digital channels.',
  solution: 'Implemented Shopilots AI agents across their website chat, Instagram, and WhatsApp Business channels to provide personalized styling advice and product recommendations.',
  results: ['47% increase in conversion rate', '32% higher average order value', '58% reduction in return rate', '3.2x ROI within first 3 months'],
  testimonial: {
    quote: 'Shopilots transformed how we engage with customers. Our AI stylist now handles thousands of conversations daily, providing personalized recommendations that truly resonate with shoppers. The reduction in returns alone made this investment worthwhile.',
    author: 'Emma Richardson',
    title: 'Digital Commerce Director, LuxeStyle'
  },
  conversation: [{
    role: 'user',
    message: 'I need a dress for a summer wedding, something elegant but not too formal.'
  }, {
    role: 'assistant',
    message: "I'd be happy to help you find the perfect dress! Could you tell me a few more details? What's your preferred color palette, and are you looking for something more fitted or flowy?"
  }, {
    role: 'user',
    message: "I like blues and greens, and prefer something flowy as it's going to be hot."
  }, {
    role: 'assistant',
    message: "Based on your preferences, I recommend our Sophia Chiffon Maxi Dress in teal or the Aria Floral Wrap Dress in blue. Both are lightweight, flowy, and perfect for summer weddings. The Sophia has elegant draping, while the Aria has a beautiful floral pattern that's festive without being too formal. Would you like to see these options?"
  }]
}, {
  id: 'electronics',
  title: 'TechWorld Boosts Sales by 53% with AI-Powered Product Recommendations',
  category: 'Consumer Electronics',
  company: 'TechWorld',
  logo: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  image: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2101&q=80',
  challenge: 'TechWorld faced challenges helping customers navigate complex technical specifications and choose the right products from their extensive catalog of over 5,000 items.',
  solution: 'Deployed Shopilots AI agents on their website, ChatGPT plugin, and customer service channels to provide expert technical guidance and personalized product recommendations.',
  results: ['53% increase in online conversion rate', '41% reduction in support tickets', '28% increase in customer satisfaction scores', '67% of customers reported finding better products than they initially considered'],
  testimonial: {
    quote: "Our customers often felt overwhelmed by technical jargon and the sheer number of options. Shopilots' AI agents translate complex specs into benefits customers actually care about. The impact on our sales and customer satisfaction has been remarkable.",
    author: 'Michael Chen',
    title: 'CTO, TechWorld'
  },
  conversation: [{
    role: 'user',
    message: "I need a new TV for my living room. It's pretty bright with large windows."
  }, {
    role: 'assistant',
    message: "For bright rooms with large windows, you'll want a TV with high brightness (nits) and good anti-glare properties. What size are you looking for, and what's your budget range?"
  }, {
    role: 'user',
    message: 'Around 65 inches, and I can spend up to $1,200.'
  }, {
    role: 'assistant',
    message: "Based on your requirements, I'd recommend the Samsung QN65Q70A QLED TV at $1,099. It offers excellent brightness (1,500 nits), anti-glare technology, and Quantum HDR that performs well in bright rooms. Another great option is the Sony X85J at $1,199 with its X1 processor and anti-reflection coating. Both have excellent reviews for bright room performance. Would you like more details on either of these models?"
  }]
}, {
  id: 'home',
  title: 'HomeEssentials Achieves 62% Growth in Social Commerce with AI Agents',
  category: 'Home Appliances',
  company: 'HomeEssentials',
  logo: 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  challenge: 'HomeEssentials struggled to effectively sell complex home appliances through social media channels and saw low engagement on product posts.',
  solution: 'Implemented Shopilots AI agents to respond to comments on Instagram and Facebook posts, answer product questions in direct messages, and guide customers through appliance selection.',
  results: ['62% increase in social media-driven sales', '156% higher engagement on product posts', '43% reduction in sales cycle length', '78% of social inquiries now handled automatically'],
  testimonial: {
    quote: "Before Shopilots, our social media was mostly one-way communication. Now it's a powerful sales channel. Our AI agents respond instantly to comments and DMs with helpful, accurate information about our appliances. The conversation rate from social media comment to purchase has been incredible.",
    author: 'Sarah Johnson',
    title: 'Social Media Director, HomeEssentials'
  },
  conversation: [{
    role: 'user',
    message: 'Looking for a new refrigerator for a small kitchen. Any recommendations?'
  }, {
    role: 'assistant',
    message: "For small kitchens, I'd recommend a counter-depth refrigerator to save space while maximizing storage. What dimensions would work for your space, and do you have any specific features you're looking for?"
  }, {
    role: 'user',
    message: "I have about 33 inches width available, and I'd like something with a water dispenser."
  }, {
    role: 'assistant',
    message: 'The GE Profile PYE22KYNFS would be perfect for your space at 32.75" wide. It\'s counter-depth, has an internal water dispenser, and offers excellent organization features. Another great option is the Samsung RF23A9671SR at 32.5" wide, which has a water dispenser and innovative FlexZone drawer. Both are Energy Star certified to help keep utility bills down. Would you like more details on either model?'
  }]
}, {
  id: 'cosmetics',
  title: 'GlowBeauty Transforms Customer Experience with AI Beauty Advisors',
  category: 'Cosmetics & Beauty',
  company: 'GlowBeauty',
  logo: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
  image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2087&q=80',
  challenge: 'GlowBeauty struggled with helping online customers find the right skincare and makeup products for their specific skin types and concerns without in-person consultations.',
  solution: 'Deployed Shopilots AI beauty advisors across their website, WhatsApp, and Instagram to provide personalized skincare and makeup recommendations based on customer needs.',
  results: ['58% increase in customer satisfaction', '44% higher conversion rate', '37% increase in repeat purchases', '51% reduction in product returns'],
  testimonial: {
    quote: 'Our AI beauty advisors have revolutionized how we connect with customers online. They provide the personalized consultation experience that was previously only possible in-store. Customers love getting tailored recommendations that actually work for their skin concerns and makeup preferences.',
    author: 'Jennifer Lopez',
    title: 'Customer Experience Director, GlowBeauty'
  },
  conversation: [{
    role: 'user',
    message: 'I have combination skin with some acne. What skincare routine would you recommend?'
  }, {
    role: 'assistant',
    message: "For combination skin with acne concerns, I'd recommend a gentle balancing approach. What's your current routine, and are there any ingredients your skin particularly loves or reacts badly to?"
  }, {
    role: 'user',
    message: 'I just use a basic cleanser and moisturizer now. My skin gets irritated by heavy fragrances.'
  }, {
    role: 'assistant',
    message: 'I recommend our Balanced Clarity line: start with the Gentle Foaming Cleanser with salicylic acid (AM/PM), follow with the Balancing Toner (fragrance-free) to restore pH, then use our Oil-Control Gel Moisturizer in the morning and Repair Serum with niacinamide at night. The Spot Treatment Gel can be applied directly to breakouts as needed. All products are fragrance-free and formulated specifically for combination skin with acne concerns.'
  }]
}, {
  id: 'apparel',
  title: 'ActiveWear Increases Engagement by 83% with WhatsApp Shopping',
  category: 'Sports Apparel',
  company: 'ActiveWear',
  logo: 'https://images.unsplash.com/photo-1529374255404-311a2a4f1fd9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
  image: 'https://images.unsplash.com/photo-1434682881908-b43d0467b798?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80',
  challenge: 'ActiveWear wanted to reach customers on messaging platforms they already use and provide personalized athletic wear recommendations based on specific sports and activities.',
  solution: 'Implemented Shopilots AI agents on WhatsApp to create a conversational shopping experience where customers could get expert advice on athletic wear for different activities.',
  results: ['83% increase in customer engagement', '49% higher conversion from WhatsApp conversations', '31% increase in average order value', '64% of customers reported finding better products for their needs'],
  testimonial: {
    quote: "WhatsApp is where our customers already spend their time, and now it's also where they shop with us. Our AI agents understand the technical requirements of different sports and activities, helping customers find the perfect gear. The convenience factor has been a game-changer for our business.",
    author: 'David Martinez',
    title: 'Head of Digital Commerce, ActiveWear'
  },
  conversation: [{
    role: 'user',
    message: 'I need some running shirts for hot weather. I sweat a lot.'
  }, {
    role: 'assistant',
    message: "For hot weather running and heavy sweating, you'll want shirts with excellent moisture-wicking properties and breathability. Do you prefer a looser fit or more compression? And are you looking for short sleeve, tank tops, or long sleeve options?"
  }, {
    role: 'user',
    message: 'Short sleeve with a looser fit would be best.'
  }, {
    role: 'assistant',
    message: "I recommend our UltraDry Performance Tee in our lightweight mesh fabric. It has our most advanced moisture-wicking technology, strategic ventilation zones, and anti-odor treatment. The relaxed fit allows for better airflow while running. For hot weather, the reflective white or light blue colors would help keep you cooler. They're currently $32.99 each or 3 for $85. Would you like to see some options?"
  }]
}];
const CaseStudies = () => {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  return <main className="py-16 bg-white" data-id="element-989">
      {/* SEO Meta Tags would go in the head */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-990">
        <div className="text-center mb-16" data-id="element-991">
          <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium" data-id="element-992">
            Success Stories
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mt-6 mb-6" data-id="element-993">
            How Leading Retailers Transform Sales
            <br data-id="element-994" />
            <span className="text-primary" data-id="element-995">With AI Shopping Advisors</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-996">
            Discover how businesses across different industries have
            revolutionized their customer experience and boosted sales with
            Shopilots AI agents.
          </p>
        </div>
        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-997">
          {caseStudies.map(study => <div key={study.id} className="bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow" data-id="element-998">
              <div className="h-48 overflow-hidden" data-id="element-999">
                <img src={study.image} alt={study.company} className="w-full h-full object-cover transition-transform hover:scale-105" data-id="element-1000" />
              </div>
              <div className="p-6" data-id="element-1001">
                <div className="flex items-center mb-4" data-id="element-1002">
                  <span className="bg-primary/10 text-primary text-xs px-3 py-1 rounded-full" data-id="element-1003">
                    {study.category}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-1004">{study.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-2" data-id="element-1005">
                  {study.challenge}
                </p>
                <a href={`#${study.id}`} className="text-primary font-medium flex items-center" data-id="element-1006">
                  Read Case Study
                  <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-1007" />
                </a>
              </div>
            </div>)}
        </div>
        {/* Individual Case Studies */}
        {caseStudies.map(study => <div key={study.id} id={study.id} className="mb-24 scroll-mt-24" data-id="element-1008">
            <div className="bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden" data-id="element-1009">
              <div className="md:flex" data-id="element-1010">
                <div className="md:w-1/2" data-id="element-1011">
                  <img src={study.image} alt={study.company} className="w-full h-full object-cover" data-id="element-1012" />
                </div>
                <div className="md:w-1/2 p-8" data-id="element-1013">
                  <div className="flex items-center mb-4" data-id="element-1014">
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm" data-id="element-1015">
                      {study.category}
                    </span>
                  </div>
                  <h2 className="text-3xl font-bold mb-4" data-id="element-1016">{study.title}</h2>
                  <div className="mb-6" data-id="element-1017">
                    <h3 className="text-lg font-semibold mb-2" data-id="element-1018">Challenge</h3>
                    <p className="text-gray-600" data-id="element-1019">{study.challenge}</p>
                  </div>
                  <div className="mb-6" data-id="element-1020">
                    <h3 className="text-lg font-semibold mb-2" data-id="element-1021">Solution</h3>
                    <p className="text-gray-600" data-id="element-1022">{study.solution}</p>
                  </div>
                  <div data-id="element-1023">
                    <h3 className="text-lg font-semibold mb-2" data-id="element-1024">Results</h3>
                    <ul className="space-y-2" data-id="element-1025">
                      {study.results.map((result, index) => <li key={index} className="flex items-start" data-id="element-1026">
                          <CheckIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" data-id="element-1027" />
                          <span className="ml-3 text-gray-600" data-id="element-1028">{result}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>
              {/* Testimonial */}
              <div className="bg-gray-50 p-8 border-t border-gray-100" data-id="element-1029">
                <div className="flex items-start" data-id="element-1030">
                  <div className="flex-shrink-0 mr-4" data-id="element-1031">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center" data-id="element-1032">
                      <span className="text-primary font-semibold" data-id="element-1033">
                        {study.testimonial.author.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div data-id="element-1034">
                    <div className="flex mb-2" data-id="element-1035">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-yellow-400" data-id="element-1036" />)}
                    </div>
                    <p className="text-gray-600 italic mb-4" data-id="element-1037">
                      "{study.testimonial.quote}"
                    </p>
                    <p className="font-medium" data-id="element-1038">{study.testimonial.author}</p>
                    <p className="text-sm text-gray-500" data-id="element-1039">
                      {study.testimonial.title}
                    </p>
                  </div>
                </div>
              </div>
              {/* Conversation Example */}
              <div className="p-8 border-t border-gray-100" data-id="element-1040">
                <h3 className="text-xl font-semibold mb-4" data-id="element-1041">
                  AI Shopping Advisor in Action
                </h3>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-1042">
                  <div className="space-y-4" data-id="element-1043">
                    {study.conversation.map((msg, index) => <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1044">
                        <div className={`rounded-lg px-4 py-2 max-w-md shadow-sm ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-white border border-gray-200'}`} data-id="element-1045">
                          <p className="text-sm" data-id="element-1046">{msg.message}</p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>)}
        {/* CTA Section */}
        <div className="bg-primary/5 rounded-2xl p-8 md:p-12 text-center" data-id="element-1047">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1048">
            Ready to transform your retail experience?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1049">
            Join these forward-thinking retailers using AI to boost sales and
            enhance customer experience. Start your free trial today.
          </p>
          <Button variant="primary" size="lg" onClick={() => setIsWizardOpen(true)} data-id="element-1050">
            Start Free Trial
          </Button>
        </div>
      </div>
      <Wizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} data-id="element-1051" />
    </main>;
};
export default CaseStudies;